import {
  Alert,
  AlertTitle,
  Box,
  Chip,
  Divider,
  Drawer,
  Stack,
  Typography,
} from "@mui/material";

import { DrawerHead, Term, Tooltip } from "@lopie/kernel-ui";

import { EGRESS_DOES_NOT_PROVE, approvedEgress } from "../sections/sandbox.js";

/**
 * One app's whole ceiling, opened from the register (LO-111).
 *
 * ## Why this is a drawer now
 *
 * Every fact in here was on the card. Fourteen apps, eight labelled blocks
 * each, and the Apps screen was **6,617 pixels tall** — so the answer to
 * "what may this app touch" was somewhere in the middle of a page nobody
 * could hold in their head, and the four apps a person actually operates were
 * indistinguishable from the ten that came built in.
 *
 * The rule the kit states: high level first, the detail one click in. The
 * card answers *what is this, how much may it do, is any of it running, does
 * it reach further unattended*. This answers everything else, and it answers
 * it in full — nothing here is summarised, because this is the place the
 * summary points at.
 *
 * ## Three things it still refuses to flatten
 *
 * **`reads` and `service_reads` are never summed.** With no user behind a
 * request, `manifest ∩ user` collapses to `manifest`, so a service
 * declaration wider than the interactive one is how the intersection gets
 * bypassed. Two blocks, two headings, and a warning between them when the
 * kernel says one reaches past the other.
 *
 * **The write side gets the same weight as the read side** (LO-108). The
 * sentence a customer needs about a pager is that a monitor holding a key
 * this kernel never sees can cause the app to text every employee it can see,
 * and every other fact here is about what a *person* reaches through the app.
 *
 * **An agent is not a second principal.** The list is here, under the ceiling
 * it acts inside, rather than on a screen of its own — a separate Agents
 * screen would read as a separate kind of principal, which is exactly what an
 * agent is not.
 */
export default function AppDrawer({
  app,
  entry,
  children,
  qa,
  onClose,
}) {
  if (!app) return <Drawer anchor="right" open={false} onClose={onClose} />;

  const wider = app.service_wider_than_user || [];
  const egress = approvedEgress(app);

  // A set difference of two published lists, computed here rather than in the
  // kernel. `service_wider_than_user` is the kernel's because the read side
  // needs tag intersection to decide it; this one needs neither, and a round
  // trip to subtract two arrays would be ceremony.
  const widerWrites = (app.service_actions || []).filter(
    (a) => !(app.actions || []).includes(a),
  );

  return (
    <Drawer
      anchor="right"
      open={Boolean(app)}
      onClose={onClose}
      slotProps={{
        paper: { sx: { width: { xs: "100%", sm: 620 }, maxWidth: "100%" } },
      }}
    >
      <Box sx={{ p: { xs: 2, sm: 3 }, minWidth: 0 }}>
        <DrawerHead
          title={app.label || app.app_type || app.app}
          onClose={onClose}
          // The id under the name whenever there is a name above it. A card
          // can afford to show only the friendly one; in here somebody is
          // reading a ceiling, and the string that ceiling is keyed by
          // belongs on the same screen.
          //
          // The sign-in id does *not* go on this line. It is a 26-character
          // Cognito string, it is the thing the Access screen and the record
          // of changes both key by, and printed bare beside the name it is
          // exactly the id-with-no-context this pass exists to remove. It
          // moves below, where there is room to say what it is for.
          id={app.app_type || app.app}
          chips={
            <>
              <Tooltip title="Which version of this app's permissions is in force. It changes when somebody reviews and commits a change to what the app may touch.">
                <Chip
                  size="small"
                  variant="outlined"
                  label={`permissions v${app.version}`}
                />
              </Tooltip>
              {entry?.defaultVersion ? (
                <Tooltip title="The version of the app itself that the library installs. A different thing from the permissions version beside it, and it moves for different reasons.">
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`installs ${entry.defaultVersion}`}
                  />
                </Tooltip>
              ) : null}
              {app.is_self ? (
                <Tooltip title="You are signed into this app right now. It is listed here like any other, because a console missing from the register it displays would be a back door with no entry.">
                  <Chip
                    size="small"
                    label="this console"
                    variant="outlined"
                    sx={{
                      color: "kernel.permitted",
                      borderColor: "kernel.permitted",
                    }}
                  />
                </Tooltip>
              ) : null}
            </>
          }
          lede={
            <>
              {entry?.description ? `${entry.description} ` : ""}
              This is the full limit on one app: everything it may look at,
              everything it may change, and whether any of that is wider when
              it runs on its own than when a person is driving it. It is a
              read — the limit itself is set in a reviewed file, and nothing
              on this panel changes it.
            </>
          }
        />

        {app.app && app.app !== (app.app_type || app.app) ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
            When this app signs in it identifies itself as{" "}
            <Box component="span" sx={{ fontFamily: "monospace" }}>
              {app.app}
            </Box>
            . That is the string you will see beside it on the Access screen
            and in the record of every change it makes.
          </Typography>
        ) : null}

        <Divider sx={{ mb: 2.5 }} />

        {/* What is running, first. It is the half a person can change, and
            the only half this console can do anything about. */}
        <Heading note="Copies of this app that are actually deployed and reachable. The permissions below apply to every one of them.">
          What you have running
        </Heading>
        <Box sx={{ mb: 3 }}>{children}</Box>

        {/* Custom apps only — `qa` is only ever passed for an app-* app that
            was requested rather than committed, the same split the
            Custom/Library tabs make. A committed app has no build to
            re-review and no author to file a fix under. */}
        {qa ? (
          <>
            <Heading note="Whether this app's current build and live instance actually work, follow this repo's design rules, and are safe to grant what they asked for. Runs under the app-qa agent's own manifest, never this console's.">
              QA
            </Heading>
            <Box sx={{ mb: 3 }}>{qa}</Box>
          </>
        ) : null}

        <Heading
          note={
            <>
              When somebody is signed in, what the app reaches is the narrower
              of two limits: what the app is permitted, and what that
              particular person is permitted. This is the app&apos;s half.
            </>
          }
        >
          When a person is signed in
        </Heading>
        <Box sx={{ mb: 1.5 }}>
          <Label>May look at</Label>
          <Grants
            grants={app.reads}
            empty="Nothing. A signed-in person can see no business records at all through this app."
          />
        </Box>
        <Box sx={{ mb: 3 }}>
          <Label>May change</Label>
          {app.actions?.length ? (
            <Verbs verbs={app.actions} />
          ) : (
            <Typography variant="body2" color="text.secondary">
              Nothing. This app can look and it cannot change anything.
            </Typography>
          )}
        </Box>

        <Heading
          note={
            <>
              When the app acts on its own — a nightly job, an alarm, a
              schedule — there is no person, so the second limit above is not
              there. Only the app&apos;s own <Term name="ceiling">ceiling</Term>{" "}
              applies, which is why this half is worth reading separately.
            </>
          }
        >
          When it acts on its own
        </Heading>
        <Box sx={{ mb: 1.5 }}>
          <Label>May look at</Label>
          <Grants
            grants={app.service_reads}
            empty="Nothing. This app only ever looks at records for a signed-in person — which is the narrower case, and the more usual one."
          />
        </Box>

        {wider.length ? (
          // Not an error. The point is that it is worth a look, and that
          // nothing rendered it at all until this screen existed.
          <Alert severity="warning" variant="outlined" sx={{ mb: 1.5 }}>
            <AlertTitle>
              Sees more on its own than it does for a person: {wider.join(", ")}
            </AlertTitle>
            With nobody signed in there is no second limit, so this app sees
            more of{" "}
            {wider.length === 1 ? "that kind of record" : "those kinds of record"}{" "}
            when it runs on its own than any person gets through it. Perfectly
            normal for a nightly job, and worth knowing either way.
          </Alert>
        ) : null}

        {/* Only for an app that writes at all. The vault declares no actions,
            and "never write" followed by "every write this app makes" is two
            sentences that argue with each other about an app that makes
            none. */}
        {app.actions?.length ? (
          <Box sx={{ mb: 1.5 }}>
            <Label>May change</Label>
            {app.service_actions?.length ? (
              <Verbs verbs={app.service_actions} />
            ) : (
              // Empty is the loud case rather than the quiet one, which is
              // why it has prose instead of an em dash: an app that declares
              // none cannot act with nobody behind the request at all, and
              // saying so is the difference between a reader assuming it and
              // a reader knowing it.
              <Typography variant="body2" color="text.secondary">
                Nothing. Every change this app makes has a signed-in person
                behind it, so an alarm or a schedule cannot make it act on its
                own.
              </Typography>
            )}
          </Box>
        ) : null}

        {widerWrites.length ? (
          // The write-side twin of the reads warning above, and the sharper
          // of the two: reaching further unattended means *doing* more with
          // nobody there, not merely seeing more.
          <Alert severity="warning" variant="outlined" sx={{ mb: 1.5 }}>
            <AlertTitle>
              Can change more on its own than it can for a person:{" "}
              {widerWrites.join(", ")}
            </AlertTitle>
            The app is permitted these when it runs on its own and not when a
            person is driving it — so nobody can trigger them by hand. That is
            deliberate for some jobs, and worth a second look otherwise.
          </Alert>
        ) : null}

        <Box sx={{ mb: 3 }} />

        {/* Part of what was granted (LO-175), so it sits beside the reads and
            the changes. The caveat is on the list rather than at the foot of
            the page: it qualifies these hosts and nothing else here, and it
            says what the screen states, which stays true whether or not
            anything stops a call to another host. */}
        <Heading note="The outside hosts this app's server function was approved to call. Approved with the rest of its request, and an install is held to them.">
          Can call out to
        </Heading>
        <Box sx={{ mb: 3 }}>
          {egress.none ? (
            <Typography variant="body2" color="text.secondary">
              No outbound hosts approved. This app was approved to call nothing
              outside the kernel, which is what the approval says rather than
              something missing from it.
            </Typography>
          ) : (
            <Stack
              direction="row"
              sx={{ gap: 0.75, flexWrap: "wrap" }}
              role="list"
              aria-label="approved egress hosts"
            >
              {egress.hosts.map((host) => (
                <Chip
                  key={host}
                  role="listitem"
                  size="small"
                  variant="outlined"
                  label={host}
                  // Wrapped, not clipped: a host is read letter by letter.
                  sx={{
                    fontFamily: "monospace",
                    maxWidth: "100%",
                    height: "auto",
                    "& .MuiChip-label": {
                      whiteSpace: "normal",
                      overflowWrap: "anywhere",
                      py: 0.25,
                    },
                  }}
                />
              ))}
            </Stack>
          )}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 0.75 }}
          >
            {EGRESS_DOES_NOT_PROVE}
          </Typography>
        </Box>

        <Heading note="The AI workers this app declares. They act inside everything above and add nothing to it.">
          Agents
        </Heading>
        <Agents agents={app.agents} />
      </Box>
    </Drawer>
  );
}

/**
 * A heading with a rule, and the sentence that says what the section is.
 *
 * The four labels here are about a distinction this product turns on and
 * almost nobody arrives knowing: the same app has two different reaches
 * depending on whether a person is signed in behind the request. A label
 * cannot carry that. The note is where it is actually explained.
 */
function Heading({ children, note }) {
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography
        variant="overline"
        sx={{
          display: "block",
          pb: 0.5,
          borderBottom: 1,
          borderColor: "divider",
          color: "text.primary",
        }}
      >
        {children}
      </Typography>
      {note ? (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 1, maxWidth: "62ch" }}
        >
          {note}
        </Typography>
      ) : null}
    </Box>
  );
}

function Label({ children }) {
  return (
    <Typography variant="overline" color="text.secondary" sx={{ display: "block" }}>
      {children}
    </Typography>
  );
}

/**
 * The changes an app may make, each named `thing.action`.
 *
 * The caption is not padding. `invoice.create` is self-evident to somebody
 * who has seen twenty of them and is an unreadable string to somebody who has
 * seen none — and the register is full of people in the second group. One
 * sentence, once per list, and the chips can stay as short as they are.
 */
function Verbs({ verbs }) {
  return (
    <Box>
      <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap" }}>
        {verbs.map((a) => (
          <Chip
            key={a}
            size="small"
            variant="outlined"
            label={a}
            sx={{ fontFamily: "monospace" }}
          />
        ))}
      </Stack>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mt: 0.75 }}
      >
        Each one is a single thing it may do, written as{" "}
        <em>what · to what</em>. Anything not on this list is refused.
      </Typography>
    </Box>
  );
}

/**
 * One `reads` or `service_reads` block.
 *
 * Empty is a real answer and says something specific, so it renders a
 * sentence rather than an empty box.
 *
 * A row per type rather than a table. The table was two columns — a 140px
 * monospace type name and a wrapping row of chips — which needed its own
 * horizontal scroll container at 390px, found by `layout.test.js` rather than
 * by a person. In a drawer the type can simply sit above its tags.
 */
function Grants({ grants, empty }) {
  if (!grants?.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        {empty}
      </Typography>
    );
  }
  return (
    <Stack spacing={0.75}>
      <Typography variant="caption" color="text.secondary">
        A kind of record, then the <Term name="tags">tags</Term> it is limited
        to. Permissions are written against labels rather than against
        individual records.
      </Typography>
      {grants.map((g) => (
        <Stack
          key={g.type}
          direction="row"
          spacing={1}
          sx={{ alignItems: "baseline", flexWrap: "wrap", minWidth: 0 }}
        >
          <Typography
            variant="body2"
            sx={{ fontFamily: "monospace", minWidth: 120 }}
          >
            {g.type}
          </Typography>
          <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap" }}>
            {g.tags.length ? (
              g.tags.map((t) => (
                <Chip key={t} size="small" variant="outlined" label={t} />
              ))
            ) : (
              // The counterintuitive one, and it is in the Grant docstring
              // for the same reason: an empty tag set is the least permissive
              // thing a grant can say, not the most.
              <Tooltip title="Records of this kind that carry no label at all. Counterintuitively this is the narrowest permission there is, not the widest.">
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ borderBottom: "1px dotted", borderColor: "divider", cursor: "help" }}
                >
                  untagged only
                </Typography>
              </Tooltip>
            )}
          </Stack>
        </Stack>
      ))}
    </Stack>
  );
}

/**
 * The agents one app declares, grouped by the division each one names.
 *
 * **Grouped by the exact string, and the caption says so.** `division` is a
 * free string on the manifest because an org-structure model is a real thing
 * and not this card, so `Revenue Operations` and `revenue operations` are two
 * divisions here and nothing folds them. Silently normalising would invent an
 * org chart nobody agreed to and would hide the fact that two apps disagree
 * about a name — which is a thing somebody should fix rather than a thing a
 * screen should paper over.
 */
function Agents({ agents }) {
  if (!agents?.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        None. Every change this app makes comes from its own code rather than
        from an AI worker, and the record of each change says so.
      </Typography>
    );
  }

  // Insertion order, which is the manifest's order: a reviewed file read top
  // to bottom. Sorting would reorder somebody's document on the way to the
  // screen for no reason anybody asked for.
  const divisions = [];
  for (const agent of agents) {
    const found = divisions.find((d) => d.name === agent.division);
    if (found) found.agents.push(agent);
    else divisions.push({ name: agent.division, agents: [agent] });
  }

  return (
    <Stack spacing={2}>
      {divisions.map((division) => (
        <Box key={division.name} sx={{ minWidth: 0 }}>
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ display: "block", overflowWrap: "anywhere" }}
          >
            {division.name}
          </Typography>
          <Stack spacing={0.75} sx={{ mt: 0.5 }}>
            {division.agents.map((agent) => (
              // A row rather than a table: the sentence is the long part and
              // it has to wrap, which is the thing a two-column table at
              // 390px will not do.
              <Box key={agent.name} sx={{ minWidth: 0 }}>
                {/* The label the manifest declared, with the id beside it —
                    the same pair the drawer's own header shows. An agent
                    called "Receivables Review" on one screen and
                    `receivables-review` on the next reads as two things. */}
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ alignItems: "baseline", flexWrap: "wrap", rowGap: 0.5 }}
                >
                  <Typography variant="subtitle2" sx={{ overflowWrap: "anywhere" }}>
                    {agent.label || agent.name}
                  </Typography>
                  {agent.label ? (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontFamily: "monospace", overflowWrap: "anywhere" }}
                    >
                      {agent.name}
                    </Typography>
                  ) : null}
                </Stack>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ display: "block", overflowWrap: "anywhere" }}
                >
                  {agent.does}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      ))}
      {/* Last, not first. The list is what somebody came for; this is the
          sentence that stops them reading it as a second grant, and it is
          only worth saying on a card that has agents to misread. */}
      <Typography variant="body2" color="text.secondary">
        An agent is not a second login. It can change exactly what this app
        can change, for the person in front of it, inside the limits above —
        so naming one here adds a name to the record of what was done and adds
        no permission at all. The groupings are the exact words each app
        declared; nothing here tidies spelling, so two spellings show as two
        groups.
      </Typography>
    </Stack>
  );
}
